/**
 * Runtime webpack publicPath override for portfolio subdomains.
 *
 * Code-split chunks load at runtime from `__webpack_public_path__` (CRA default
 * `/tev2/`, host-relative). Public portfolio subdomains (`<user>.contently.com`)
 * serve no `/tev2` route, so chunks 404 there → ChunkLoadError (eng-maintenance
 * #20804 / #22144). The Rails `rewrite_tev2_asset_host` shell fix can't reach these
 * URLs — webpack injects them at runtime, not into index.html. So on a subdomain we
 * repoint chunk loading at the apex origin. Must run before any lazy import resolves
 * (first import in `index.tsx`).
 *
 * Gate on the SERVED HOST, not `window.__PORTFOLIO_USERNAME__`: that flag is injected
 * only on the portfolio root (`PortfoliosController#show`), so the auth routes served
 * on the same subdomain by `PlatformWebController#show_public` (/signin, /register,
 * /users/password/*, …) lacked it and 404'd every chunk (#22144). Host-detection
 * covers all subdomain routes at once, root included.
 *
 * Apex origin comes from `window.RuntimeEnvironment.platform.host` (deploy-time,
 * per-env, injected by `public/runtime.js`). We do NOT use the build-time
 * `REACT_APP_CONTENTLY_APP_DOMAIN` as primary — the prod Dockerfile never passes it,
 * so it's `undefined` in the image and the override silently no-ops; kept only as a
 * local/dev fallback.
 */
declare let __webpack_public_path__: string;

interface RuntimeEnvironmentShape {
  platform?: { host?: string };
}

if (typeof window !== 'undefined') {
  const runtime = (window as unknown as { RuntimeEnvironment?: RuntimeEnvironmentShape })
    .RuntimeEnvironment;
  const apexOrigin = runtime?.platform?.host || process.env.REACT_APP_CONTENTLY_APP_DOMAIN;

  if (apexOrigin) {
    let apexHostname: string | undefined;
    try {
      apexHostname = new URL(apexOrigin).hostname;
    } catch {
      // Malformed origin — leave publicPath at the CRA default rather than throwing.
      apexHostname = undefined;
    }

    // Fire only on a subdomain of the apex host. The leading dot prevents a look-alike
    // suffix host (`evilcontently.com`) matching `contently.com`; apex and local/dev
    // are no-ops (equal host / not a subdomain).
    const servedHost =
      typeof window.location?.hostname === 'string' ? window.location.hostname : '';
    const onPortfolioSubdomain =
      !!apexHostname && servedHost !== apexHostname && servedHost.endsWith(`.${apexHostname}`);

    if (onPortfolioSubdomain) {
      __webpack_public_path__ = `${apexOrigin.replace(/\/$/, '')}/tev2/`;
    }
  }
}

export {};
